// src/components/devices/BulkDeviceManager.js
import React, { useContext, useEffect, useState } from 'react';
import { toast } from '../../lib/toast';
import {
  Lightbulb, Thermometer, Settings, RefreshCw, ChevronUp, ChevronDown, Eye, EyeOff
} from 'lucide-react';
import { AppContext } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { useAuth } from '../../context/AuthContext';
import {
  PREDEFINED_DEVICE_NAMES,
  PREDEFINED_LOCATIONS,
  PREDEFINED_FLOORS,
  PREDEFINED_DEVICE_TYPES
} from '../../lib/deviceConstants.js';

const BulkDeviceManager = () => {
  const { devices, updateDeviceMetadata, controlDevice, refreshDevices } = useContext(AppContext);
  const { apiRequest } = useAuth();

  const [, setMetadata] = useState({ locations: [], deviceTypes: [], floors: [] });
  const [bulkDevices, setBulkDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [predefinedData, setPredefinedData] = useState({
    deviceNames: PREDEFINED_DEVICE_NAMES,
    locations: PREDEFINED_LOCATIONS,
    floors: PREDEFINED_FLOORS,
    deviceTypes: PREDEFINED_DEVICE_TYPES
  });
  

  const API_BASE_URL = '';
  const API_PATH = '/api';

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const { response, data } = await apiRequest(`${API_BASE_URL}${API_PATH}/metadata`);
        if (response.ok) {
          setMetadata(data);
          setPredefinedData(prev => ({
            ...prev,
            locations: [...new Set([...prev.locations, ...(data.locations || [])])],
            floors: [...new Set([...prev.floors, ...(data.floors || [])])],
            deviceTypes: [...new Set([...prev.deviceTypes, ...(data.deviceTypes || [])])]
          }));
        }
      } catch (error) {
        console.error('Error fetching metadata:', error);
      }
    };
    fetchMetadata();
  }, [apiRequest]);

  useEffect(() => {
    if (devices.length > 0) {
      setBulkDevices(prev => {
        return devices.map(device => {
          const existing = prev.find(d => d.id === device.id);
          if (existing) {
            const updatedDevice = {
              ...existing,
              ...device,
              controls: device.controls || [],
              sensors: device.sensors || [],
              name: existing.name,
              location: existing.location,
              floor: existing.floor,
              type: existing.type,
              hasChanges: existing.hasChanges,
              isAssigned: existing.isAssigned
            };
            return updatedDevice;
          } else {
            return {
              id: device.id,
              parentId: device.parentId,
              originalName: device.name,
              name: device.name ?? '',
              location: device.location ?? '',
              floor: device.floor ?? '',
              type: device.type ?? '',
              deviceType: device.type ?? '',
              hasChanges: false,
              isAssigned: !!(device.name && device.location && device.type),
              controls: device.controls || [],
              sensors: device.sensors || [],
              subtype: device.subtype || ''
            };
          }
        });
      });
    }
  }, [devices]);

  const saveDeviceChanges = async (device) => {
    try {
      const metadata = {
        name: device.name,
        location: device.location,
        type: device.type,
        floor: device.floor
      };
      await updateDeviceMetadata(device.id, metadata);
      setBulkDevices(prev =>
        prev.map(d => d.id === device.id ? { ...d, hasChanges: false, originalName: device.name } : d)
      );
      // toast.success(`Saved: ${device.name}`);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(`Failed to save ${device.name}`);
    }
  };

  const handleFieldChange = (deviceId, field, value) => {
    setBulkDevices(prev => {
      const updatedDevices = prev.map(device => {
        if (device.id === deviceId) {
          const updated = { ...device, [field]: value, hasChanges: true };
          updated.isAssigned = !!(updated.name && updated.location && updated.type);
          return updated;
        }
        return device;
      });

      const changedDevice = updatedDevices.find(d => d.id === deviceId);
      if (changedDevice) saveDeviceChanges(changedDevice);

      return updatedDevices;
    });
  };

  const handleRefreshDevices = async () => {
    try {
      setIsLoading(true);
      await refreshDevices();
      // toast.success('Device status updated');
    } catch (error) {
      console.error('Refresh error:', error);
      toast.error('Failed to refresh devices');
    } finally {
      setIsLoading(false);
    }
  };

  const getDeviceIcon = (device) => {
    const hasCurtainControl = device.controls?.find(c => 
      c.property === 'state' && 
      c.type === 'enum' && 
      Array.isArray(c.values) &&
      c.values.includes('OPEN') && 
      c.values.includes('CLOSE')
    );
    
    if (hasCurtainControl) {
      return <Settings className="w-5 h-5 text-muted-foreground" />;
    }

    const type = device.type?.toLowerCase();
    switch (type) {
      case 'light': 
      case 'switch': 
        return <Lightbulb className="w-5 h-5 text-muted-foreground" />;
      case 'sensor': 
        const presenceSensor = device.sensors?.find(s => s.property === 'presence');
        if (presenceSensor) {
          const isPresent = presenceSensor.value === true;
          return isPresent ? <Eye className="w-5 h-5 text-primary" /> : <EyeOff className="w-5 h-5 text-muted-foreground" />;
        }
        return <Settings className="w-5 h-5 text-muted-foreground" />;
      case 'thermostat': 
        return <Thermometer className="w-5 h-5 text-muted-foreground" />;
      case 'curtain':
        return <Settings className="w-5 h-5 text-muted-foreground" />;
      default:
        return <Settings className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const unassignedDevices = bulkDevices.filter(d => !d.isAssigned);
  const assignedDevices = bulkDevices.filter(d => d.isAssigned);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold tracking-tight">Device Management</h2>
        <Button
          onClick={handleRefreshDevices}
          disabled={isLoading}
          className="h-11 min-w-[44px]"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {[{ label: 'Unassigned', list: unassignedDevices }, { label: 'Assigned', list: assignedDevices }]
        .filter(section => section.list.length > 0)
        .map(({ label, list }) => (
          <Card key={label} className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium">
                {label} Devices
                <span className="ml-2 text-sm text-muted-foreground">({list.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {list.map(device => (
                <DeviceCard
                  key={device.id}
                  device={device}
                  predefinedData={predefinedData}
                  onFieldChange={handleFieldChange}
                  onControl={controlDevice}
                  getDeviceIcon={getDeviceIcon}
                  isLoading={isLoading}
                />
              ))}
            </CardContent>
          </Card>
        ))}
    </div>
  );
};

const DeviceCard = ({ device, predefinedData, onFieldChange, onControl, getDeviceIcon, isLoading }) => {
  const [loading, setLoading] = useState(false);

  const handleControlChange = async (property, value) => {
    try {
      setLoading(true);
      await onControl(device.id, property, value, device.parentId);
    } catch (error) {
      console.error(`Error controlling ${device.name}:`, error);
      toast.error(`Failed to control ${device.name || device.id}`);
    } finally {
      setLoading(false);
    }
  };

  const renderControl = () => {
    const type = device.type?.toLowerCase();
    const presenceSensor = device.sensors?.find(s => s.property === 'presence');
    
    if (type === 'sensor' || (!type && presenceSensor)) {
      const presence = presenceSensor?.value === true;
      return (
        <div className="flex items-center gap-2">
          {presence ? <Eye className="w-4 h-4 text-primary" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
          <span className="text-sm text-muted-foreground">{presence ? 'Present' : 'Not Present'}</span>
        </div>
      );
    }

    if (!device.controls || device.controls.length === 0) return null;

    const hasHeatingSetpoint = device.controls?.find(c =>
      c.property?.includes('current_heating_setpoint')
    );

    if (type === 'thermostat' || (!type && hasHeatingSetpoint)) {
      const powerControl = device.controls.find(c => c.valueType === 'binary');
      if (powerControl) {
        const isOn = powerControl.value === true || powerControl.value === 'ON' || powerControl.value === powerControl.options?.on;
        const toggleValue = isOn ? powerControl.options?.off || false : powerControl.options?.on || true;

        return (
          <Button
            variant={isOn ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleControlChange(powerControl.property, toggleValue)}
            disabled={loading || isLoading}
            className="h-11 min-w-[44px]"
          >
            <Thermometer className="mr-2 h-4 w-4" />
            {isOn ? 'ON' : 'OFF'}
          </Button>
        );
      }
      return null;
    }

    const curtainControl = device.controls?.find(c =>
      c.property === 'state' &&
      c.type === 'enum' &&
      (Array.isArray(c.values) || Array.isArray(c.options?.values)) &&
      ((c.values && c.values.includes('OPEN') && c.values.includes('CLOSE')) ||
       (c.options?.values && c.options.values.includes('OPEN') && c.options.values.includes('CLOSE')))
    );

    if (curtainControl) {
      const currentState = curtainControl.value;
      return (
        <div className="flex gap-2">
          <Button
            variant={currentState === 'OPEN' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleControlChange(curtainControl.property, 'OPEN')}
            disabled={loading || isLoading || currentState === 'OPEN'}
            className="h-11 min-w-[44px] flex-1"
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            variant={currentState === 'CLOSE' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleControlChange(curtainControl.property, 'CLOSE')}
            disabled={loading || isLoading || currentState === 'CLOSE'}
            className="h-11 min-w-[44px] flex-1"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      );
    }

    const binaryControl = device.controls.find(c => c.valueType === 'binary');
    if (binaryControl) {
      const isOn = binaryControl.value === true || binaryControl.value === 'ON' || binaryControl.value === binaryControl.options?.on;
      const toggleValue = isOn ? binaryControl.options?.off || false : binaryControl.options?.on || true;

      return (
        <Button
          variant={isOn ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleControlChange(binaryControl.property, toggleValue)}
          disabled={loading || isLoading}
          className="h-11 min-w-[44px]"
        >
          {isOn ? 'ON' : 'OFF'}
        </Button>
      );
    }

    return null;
  };

  return (
    <Card className="border border-border/60 bg-card">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            {getDeviceIcon(device)}
          </div>
          <div className="flex-1 min-w-0 space-y-1">
            <div className="text-sm font-medium truncate">
              {device.name || device.originalName || 'Unnamed Device'}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {device.location || 'No location'} • {device.floor || 'No floor'} • {device.type || 'No type'}
            </div>
            <div className="text-xs text-muted-foreground font-mono truncate">
              {device.id}
            </div>
          </div>
          <div className="flex-shrink-0">
            {renderControl()}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Name</label>
            <select
              value={device.name}
              onChange={e => onFieldChange(device.id, 'name', e.target.value)}
              className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select Name</option>
              {predefinedData.deviceNames.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Location</label>
            <select
              value={device.location}
              onChange={e => onFieldChange(device.id, 'location', e.target.value)}
              className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select Location</option>
              {predefinedData.locations.map(location => <option key={location} value={location}>{location}</option>)}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Floor</label>
            <select
              value={device.floor}
              onChange={e => onFieldChange(device.id, 'floor', e.target.value)}
              className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select Floor</option>
              {predefinedData.floors.map(floor => <option key={floor} value={floor}>{floor}</option>)}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Type</label>
            <select
              value={device.type}
              onChange={e => onFieldChange(device.id, 'type', e.target.value)}
              className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select Type</option>
              {predefinedData.deviceTypes.map(type => <option key={type} value={type}>{type}</option>)}
            </select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BulkDeviceManager;
